'use client';

import { ZipCity, type ZipCityLocation } from '@/components/ZipCity';
import { useFieldContext } from '@/lib/form/contexts';

interface ZipCityFieldProps {
  required?: boolean;
  /**
   * ZipCity appends ` *` itself when `required` — pass the bare text.
   * Falls back to ZipCity's own `locationSearch.placeholder` translation.
   */
  placeholder?: string;
  /** Fires when the visitor picks a location from the dropdown. */
  onSelect?: (loc: ZipCityLocation) => void;
  /**
   * Fires on every value change, including the label ZipCity writes into
   * the input on select — lets the caller invalidate a stale selection
   * when the visitor edits the text afterwards.
   */
  onTextChange?: (value: string) => void;
}

/**
 * `useAppForm` wrapper around the shared `<ZipCity />` autocomplete (the
 * same location search the header/location-finder use), bound to a string
 * field holding the visible input text. Which location was actually
 * *selected* is not form state — the caller tracks it via `onSelect`/
 * `onTextChange` (see `AdjustableContactForm`'s `location` field).
 */
export const ZipCityField = ({
  required,
  placeholder,
  onSelect,
  onTextChange,
}: ZipCityFieldProps) => {
  const field = useFieldContext<string>();
  const error = field.state.meta.errors[0]?.message;
  return (
    <ZipCity
      value={field.state.value}
      onValueChange={(value) => {
        field.handleChange(value);
        onTextChange?.(value);
      }}
      onSelect={onSelect}
      onBlur={field.handleBlur}
      error={error}
      required={required}
      placeholder={placeholder}
      // Sized like the sibling FormInput-based fields, not the larger
      // standalone location search.
      variant="formField"
    />
  );
};
